import { useContext, useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { CREATE_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	EDITOR_ONBOARDING_BLUEPRINT_KEY,
	EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import type { Creatable, CanHandle, Handle } from '../createContentFromTemplateTypes.ts';
import { processDraftUrl } from '../helpers.ts';
import { GetPersonalSpaceKeyQuery } from '../queries/GetPersonalSpaceKeyQuery.graphql.ts';
import { useExperimentalPublishPageByBlueprintMutation } from '../queries/useExperimentalPublishPageByBlueprintMutation.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import {
	type GetPersonalSpaceKeyQuery$data,
	type GetPersonalSpaceKeyQuery$variables,
} from '../queries/__generated__/GetPersonalSpaceKeyQuery.graphql.ts';
export const useEditorOnboardingBlueprintCreatable = (): Creatable => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isLicensed } = useSessionData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [createFromBlueprintMutation] = useExperimentalPublishPageByBlueprintMutation();

	// this has to be done via getApolloClient() because neither useQuery or useLazyQuery return a promise with the data, they return void.
	const getPersonalSpaceKey = useCallback(async () => {
		let personalSpaceData;
		if (isLicensed) {
			try {
				personalSpaceData = (
					await queryInterop<GetPersonalSpaceKeyQuery$data, GetPersonalSpaceKeyQuery$variables>({
						query: GetPersonalSpaceKeyQuery,
					})
				).data;
			} catch (personalSpaceError) {
				markErrorAsHandled(personalSpaceError);
				getMonitoringClient().submitError(personalSpaceError, {
					attribution: Attribution.CE_MAU,
				});

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'getPersonalSpaceKey',
						actionSubjectId: 'editorOnboardingTutorial',
						source: 'useEditorOnboardingBluePrintCreatable',
					},
				}).fire();
			}
		}

		const hasPersonalSpace = personalSpaceData?.user?.confluence?.hasPersonalSpace;
		const personalSpaceKey = hasPersonalSpace
			? personalSpaceData?.user?.confluence?.space?.key
			: undefined;

		return { hasPersonalSpace, personalSpaceKey };
	}, [isLicensed, createAnalyticsEvent]);
	const canHandle: CanHandle = useCallback((template) => {
		return (
			template.blueprintModuleCompleteKey === EDITOR_ONBOARDING_BLUEPRINT_KEY ||
			template.blueprintModuleCompleteKey === EDITOR_ONBOARDING_BLUEPRINT_KEY_LIVE_DOCS_ENABLED
		);
	}, []);

	const handle: Handle = useCallback(
		async (template, spaceKey, options = {}) => {
			const { title, parentPageId, subType } = options;
			/*
			 * This will create the draft under the root directory of the user's personal space.
			 * Fallback to current spaceKey (should be rare unless GetPersonalSpaceKeyQuery fails or
			 * when user immediately clicks on Editor Tutorial Template before loading is done).
			 */
			const { personalSpaceKey } = await getPersonalSpaceKey();
			const createFromBlueprintResponse = await createFromBlueprintMutation({
				variables: {
					spaceKey: personalSpaceKey || spaceKey,
					title,
					subType,
					parentPageId: personalSpaceKey ? undefined : parentPageId,
					blueprint: JSON.stringify(template),
					attemptSpaUrlResponse: false,
				},
			});

			const draftUrl =
				createFromBlueprintResponse?.data?.experimentalPublishPageByBlueprint?.links?.draftUrl;
			window.open(processDraftUrl(draftUrl), '_blank', 'noreferrer');

			experienceTracker.succeed({
				name: CREATE_PAGE_EXPERIENCE,
			});
			return true;
		},
		[createFromBlueprintMutation, experienceTracker, getPersonalSpaceKey],
	);

	return useMemo(() => ({ canHandle, handle }), [canHandle, handle]);
};
